/**
 * 自社設定を、**変更する単位**で切り分ける。
 *
 * ## なぜ切るのか
 *
 * 切る前は、閲覧の画面が縦 6,340px（画面9枚ぶん）あり、
 * その中に**保存できるものと、見るだけのものが混ざっていた。**
 * 保存ボタンは5種類あって、押す場所が右・左・右・中とばらばら。
 *
 * 直すほうも1枚で、**電話番号を1つ直したいだけでも
 * 1,974行の画面を開いて全項目の中から探す**ことになっていた。
 *
 * ## 切り方
 *
 * **保存の単位と画面の単位を合わせる。**1画面＝1つの決定ボタン。
 * どの画面でもボタンは同じ場所に出る（SettingEditLayout が持つ）。
 *
 * ## 全部を送るのは変えない
 *
 * サーバの `/setting/update` は全項目を受け取って会社の行を作り直す。
 * **担当ぶんだけ送ると、送らなかった項目が消える。**
 * だから画面を切っても、保存のときは読み込んだ全項目をそのまま送り、
 * 自分の担当だけ上書きする（SettingEditLayout がやる）。
 *
 * ここに書いていない項目は、どの画面からも編集できない。
 * **足したら必ずどれかの `fields` へ入れること**——
 * 入れ忘れると、画面に出ないまま毎回そのまま送り返される項目になる。
 */

/**
 * 画面に出す順。**上から使う頻度が高い順**に並べる。
 * 権限で見えない画面があっても、残りの順序は変わらない。
 */
export const SETTING_SECTIONS = Object.freeze([
  {
    key: 'company',
    title: '会社情報',
    summary: '会社名、連絡先として使用する情報です。',
    route: { name: '自社設定の会社情報' },
    fields: ['company_name', 'company_email', 'telephone']
  },
  {
    key: 'attendance',
    title: '勤怠計算・打刻',
    summary: '勤務表と打刻の会社共通の計算ルールです。',
    route: { name: '自社設定の勤怠計算' },
    fields: [
      'pre_buffer', 'post_buffer', 'work_time_unit',
      'work_basic', 'break_basic',
      'post_work_break_flg', 'use_project_break_flg',
      'half_day_leave_flg',
      'substitute_leave_expiry_flg', 'substitute_leave_expiry_months'
    ]
  },
  {
    key: 'business-hours',
    title: '就業時間',
    summary: '朝と夕方のお知らせは、ここで決めた時間を基準に届きます。',
    route: { name: '自社設定の就業時間' },
    fields: [
      'business_start_time', 'business_end_time',
      'daily_schedule_notification_lead_minutes',
      'pending_approval_notification_lead_minutes',
      'pending_leave_reminder_lead_minutes'
    ]
  },
  {
    key: 'notification',
    title: 'Slack連携・通知',
    summary: '通知の種類ごとに、オン・オフと通知先を決めます。',
    route: { name: '自社設定の通知' },
    fields: [
      'slack_token', 'clear_slack_token',
      'slack_notification_flg', 'slack_channel',
      'pending_approval_notification_flg', 'pending_approval_slack_channel',
      'management_daily_notification_flg', 'management_daily_slack_channel',
      // **法令判定を出していないあいだは、この4つは画面に出ない。**
      // 出ないだけで、保存のときは読み込んだ値をそのまま送り返す。
      'overtime_alert_notification_flg', 'overtime_alert_slack_channel',
      'overtime_calculation_due_kind', 'overtime_calculation_due_day'
    ]
  },
  {
    key: 'display',
    title: '提出物の締めと社内カレンダー',
    summary: '提出ガイドの締めと、ダッシュボードに出すカレンダーです。',
    route: { name: '自社設定の表示' },
    fields: [
      'submission_due_kind', 'submission_due_day', 'submission_due_items',
      'dashboard_calendar_embed_url'
    ]
  }
])

/**
 * サーバへ送ってはいけないもの。**表示のためだけに返ってくる。**
 *
 * 送ると、会社が決めていない値で上書きされる。
 * 36協定は専用の口があるので、この画面からは1つも送らない。
 */
export const READ_ONLY_KEYS = Object.freeze([
  'slack_feature_enabled',
  // 通知の時刻は**計算で出している**（始業/終業から前倒しを引いた結果）。
  // 列ではないので、いま送っても書かれない。それでも落とす——
  // 直すのは前倒しの分数のほうで、時刻を送り返す形にしておくと、
  // いつか受け取る側ができたときに**古い時刻で上書きする**。
  'daily_schedule_notification_time',
  'pending_approval_notification_time',
  'pending_leave_reminder_time',
  'slack_token_configured',
  'legal_compliance_enabled',
  'overtime_alert_delivery_blocker',
  'overtime_alert_uses_management_channel',
  'overtime_alert_last_send_failed',
  'overtime_alert_last_failed_month'
])

/** 36協定のものか。**この画面からは送らない。** */
export function isOvertimeAgreementKey (key) {
  return typeof key === 'string' &&
    (key.indexOf('overtime_agreement_') === 0 ||
     key.indexOf('overtime_effective_') === 0)
}

export function findSection (key) {
  return SETTING_SECTIONS.find(one => one.key === key) || null
}
